import { AxiosResponse } from 'axios';
import { CommonResponse } from '@auth_module/services';
import { adcApi, privateApi } from '@configs/axios-instances';
import {
  IOrganization,
  IOrganizationUser,
  IUpdateOrganization,
  InviteFormType,
} from '@account_module/types/Organizations';
import { ICustomer, IProductResponse, IPriceResponse } from '@app/types/Billing';

interface IUpdateSubscriptionParams {
  price_id: number;
  method: string;
}

/**
 * fetch all organizations
 * @return {Promise<IOrganization[] | undefined>}
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchOrganizations = async (): Promise<IOrganization[] | undefined> => {
  const { data } = await privateApi.get('/organizations');

  return data;
};

/**
 * Update organization
 * @param {string} id
 * @return {Promise<IOrganization>}
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchOrganizationById = async (id: string): Promise<IOrganization> => {
  const { data } = await privateApi.get(`/organizations/${id}`);

  return data;
};

/**
 * Fetch organization users
 * @param {string} id
 * @return {Promise<IOrganizationUser[]>}
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchOrganizationUsers = async (
  id: string | undefined
): Promise<IOrganizationUser[]> => {
  const { data } = await privateApi.get(`/organizations/${id}/users`);

  return data;
};

/**
 * Update organization
 * @param {string} id
 * @param {IUpdateOrganization} payload
 * @return {Promise<CommonResponse>}
 */
export const updateOrganizationName = async (
  id: string,
  payload: IUpdateOrganization
): Promise<CommonResponse> => {
  const { data } = await privateApi.put(`/organizations/${id}`, payload);

  return data;
};

/**
 * Invite user to organization
 * @param {string | undefined} id
 * @param {InviteFormType | null} payload
 * @return {Promise<CommonResponse>}
 */
export const inviteUser = async (
  id: string | undefined,
  payload: InviteFormType | null
): Promise<CommonResponse> => {
  const { data } = await privateApi.post(`/organizations/${id}/invite`, payload);

  return data;
};

/**
 * Reinvite user to organization
 * @param {string} id
 * @param {string} userId
 * @param {InviteFormType} payload
 *
 * @return {Promise<CommonResponse>}
 */
export const reInviteUser = async (
  id: string | undefined,
  userId: string | null | undefined,
  payload: InviteFormType
): Promise<CommonResponse | undefined> => {
  if (!id || !userId) return undefined;

  const { data } = await privateApi.post(`/organizations/${id}/invite/${userId}`, payload);

  return data;
};

/**
 * Remove user from organization
 * @param {string} id
 * @param {string} userId
 * @return {Promise<CommonResponse | undefined>}
 */
export const removeUser = async (
  id: string | undefined | null,
  userId: string | undefined | null
): Promise<CommonResponse | undefined> => {
  const { data } = await privateApi.delete(`/organizations/${id}/user/${userId}`);

  return data;
};

/**
 * Update user
 * @param {string} id
 * @param {string} userId
 * @param {{ [x: string]: string }} payload
 * @return {Promise<CommonResponse | undefined>}
 */
export const updateUser = async (
  id: string | undefined,
  userId: string | null,
  payload: { [x: string]: string }
): Promise<CommonResponse | undefined> => {
  const { data } = await privateApi.put(`/organizations/${id}/user/${userId}`, payload);

  return data;
};

/**
 * Fetch Customer
 * @return {Promise<AxiosResponse<ICustomer>>}
 */

export const fetchCustomer = async (): Promise<AxiosResponse<ICustomer>> => {
  return await privateApi.get('/billing/customer');
};

/**
 * Update subscription
 * @param {IUpdateSubscriptionParams} params
 * @return {Promise<CommonResponse>}
 */

export const updateSubscription = async (
  params: IUpdateSubscriptionParams
): Promise<CommonResponse> => {
  const { method, price_id } = params;

  return await privateApi.get(`/billing/${method}`, {
    params: { price_id },
  });
};

/**
 * Fetch products
 * @return {Promise<IProductResponse[]>}
 */

export const fetchProducts = async (): Promise<IProductResponse[]> => {
  const { data } = await privateApi.get('/billing/products');

  return data;
};

/**
 * Fetch products prices
 * @return {Promise<IPriceResponse[]>}
 */

export const fetchProductPrices = async (): Promise<IPriceResponse[]> => {
  const { data } = await privateApi.get('/billing/prices');

  return data;
};

/**
 * Fetch products prices
 * @param {string} params
 * @return {Promise<any>}
 */

export const fetchUsageTacking = async (params: string): Promise<any> => {
  const { data } = await adcApi.GET(`/usage-report/audio-client?${params}`);

  return data;
};
